// src/pages/Blog.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { POSTS } from "../blog/posts";
import { Search, X, Tag as TagIcon, Calendar, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("de-DE", {
            year: "numeric",
            month: "long",
            day: "2-digit",
        });
    } catch { return d || ""; }
}

const PAGE_SIZE = 9;

export default function Blog() {
    const [q, setQ] = useState("");
    const [tag, setTag] = useState("");
    const [sort, setSort] = useState("new");
    const [page, setPage] = useState(1);

    useEffect(() => {
        document.title = "Fallenjagd Blog | Tipps & Anleitungen | Tuscher Helmut";
        
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Praxis-Tipps zur Fallenjagd: Köderung, Standortwahl, Waschbär fangen, Fallenmelder nutzen. 40 Jahre Erfahrung in Artikeln zusammengefasst. Jetzt lesen!");
        }
        
        // Canonical URL
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = 'https://tuscherhelmut-betonrohrfallen.de/blog';
    }, []);

    const allTags = useMemo(() => {
        const s = new Set();
        POSTS.forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
        return Array.from(s).sort((a, b) => a.localeCompare(b, "de"));
    }, []);

    const featured = useMemo(() => {
        const sorted = [...POSTS].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        return sorted.find((p) => p.cover) || sorted[0];
    }, []);

    const filtered = useMemo(() => {
        let arr = POSTS.slice();
        if (q.trim()) {
            const s = q.toLowerCase();
            arr = arr.filter(p => (p.title || "").toLowerCase().includes(s) || (p.excerpt || "").toLowerCase().includes(s));
        }
        if (tag) arr = arr.filter(p => (p.tags || []).includes(tag));
        arr.sort((a, b) => sort === "new" ? (b.date || "").localeCompare(a.date || "") : (a.date || "").localeCompare(b.date || ""));
        return arr;
    }, [q, tag, sort]);

    const listWithoutFeatured = useMemo(() => filtered.filter((p) => p.slug !== featured?.slug), [filtered, featured]);
    const totalPages = Math.max(1, Math.ceil(listWithoutFeatured.length / PAGE_SIZE));
    const pageSafe = Math.min(page, totalPages);
    const paged = listWithoutFeatured.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

    useEffect(() => { setPage(1); }, [q, tag, sort]);

    return (
        <div className="relative overflow-hidden min-h-screen">
            {/* Background Decor */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-0 left-[20%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px]" />
                <div className="absolute bottom-0 right-[10%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px]" />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 border-b border-slate-100 dark:border-slate-800 pb-12">
                    <div className="max-w-2xl">
                        <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50 uppercase tracking-[0.2em] text-[10px]">Wissen aus 40 Jahren Praxis</Badge>
                        <h1 className="text-4xl sm:text-6xl font-heading font-black tracking-tight text-slate-900 dark:text-white uppercase leading-none">
                            Mein <span className="text-brand-600">Blog</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
                            Entdecke meine Guides, Tipps und aktuelle Neuigkeiten rund um die waidgerechte Fallenjagd.
                        </p>
                    </div>

                    {/* Filter Controls */}
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 pl-11 pr-4 py-3 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600 transition-all outline-none text-sm"
                                placeholder="Beiträge suchen..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </div>
                        <select
                            className="w-full sm:w-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500/20 outline-none cursor-pointer"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                        >
                            <option value="new">Neueste</option>
                            <option value="old">Älteste</option>
                        </select>
                    </div>
                </div>

                {/* Tags Bar */}
                <div className="flex gap-2 overflow-x-auto pb-4 mb-12 no-scrollbar">
                    <button
                        onClick={() => setTag("")}
                        className={`whitespace-nowrap px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border transition-all ${!tag ? "bg-slate-900 text-white border-slate-900 shadow-lg" : "bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-500 hover:border-slate-300"}`}
                    >
                        Alle Themen
                    </button>
                    {allTags.map((t) => (
                        <button
                            key={t}
                            onClick={() => setTag(t === tag ? "" : t)}
                            className={`whitespace-nowrap flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border transition-all ${tag === t ? "bg-brand-600 text-white border-brand-600 shadow-lg shadow-brand-500/20" : "bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-500 hover:border-slate-300"}`}
                        >
                            <TagIcon className="h-3 w-3" />
                            {t}
                        </button>
                    ))}
                </div>

                {/* Featured Post */}
                {!q && !tag && featured && (
                    <div className="mb-20" data-aos="zoom-in">
                        <Link
                            to={`/blog/${featured.slug}`}
                            className="group relative flex flex-col lg:flex-row rounded-[3rem] overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl transition-all hover:shadow-brand-500/5"
                        >
                            <div className="lg:w-3/5 aspect-[16/9] lg:aspect-auto overflow-hidden">
                                {featured.cover ? (
                                    <img src={featured.cover} alt={`${featured.title} - Fallenjagd Blog`} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" loading="lazy" />
                                ) : (
                                    <div className="h-full w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-300 italic">Kein Bild</div>
                                )}
                            </div>
                            <div className="p-8 lg:p-14 flex flex-col justify-center lg:w-2/5">
                                <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-brand-600 mb-4">
                                    <div className="h-1.5 w-1.5 rounded-full bg-brand-600 animate-pulse" />
                                    Meine Highlights
                                </div>
                                <h2 className="text-3xl sm:text-4xl font-heading font-black text-slate-900 dark:text-white leading-tight uppercase tracking-tight">
                                    {featured.title}
                                </h2>
                                <p className="mt-6 text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
                                    {featured.excerpt}
                                </p>
                                <div className="mt-8 flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-8">
                                    <div className="text-xs font-bold text-slate-400 flex items-center gap-2">
                                        <Calendar className="h-3.5 w-3.5" />
                                        Datum: {formatDate(featured.date)}
                                    </div>
                                    <span className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-900 dark:text-white group-hover:text-brand-600 transition-colors">
                                        Lesen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    </div>
                )}

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    {paged.map((p) => (
                        <article
                            key={p.slug}
                            data-aos="fade-up"
                            className="group flex flex-col rounded-[2.5rem] border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all duration-300 hover:shadow-2xl hover:shadow-brand-500/5 hover:-translate-y-1 overflow-hidden"
                        >
                            <Link to={`/blog/${p.slug}`} className="block relative aspect-[16/10] overflow-hidden">
                                {p.cover ? (
                                    <img src={p.cover} alt={`${p.title} - Tuscher Helmut Blog`} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" loading="lazy" />
                                ) : (
                                    <div className="h-full w-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-300">Kein Bild</div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Link>
                            <div className="p-8 flex flex-col flex-1">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
                                    <Calendar className="h-3 w-3" />
                                    Datum: {formatDate(p.date)}
                                </div>
                                <h3 className="text-xl font-heading font-black text-slate-900 dark:text-white uppercase leading-tight group-hover:text-brand-600 transition-colors">
                                    <Link to={`/blog/${p.slug}`}>
                                        {p.title}
                                    </Link>
                                </h3>
                                <p className="mt-4 text-sm text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed flex-1">
                                    {p.excerpt}
                                </p>
                                <div className="mt-8 pt-6 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
                                    <div className="flex gap-1">
                                        {(p.tags || []).slice(0, 1).map(t => (
                                            <span key={t} className="text-[9px] font-black uppercase tracking-widest text-brand-600 bg-brand-50 dark:bg-brand-900/20 px-2 py-0.5 rounded-lg">{t}</span>
                                        ))}
                                    </div>
                                    <Link to={`/blog/${p.slug}`} className="text-[10px] font-black uppercase tracking-widest text-slate-950 dark:text-white flex items-center gap-1 group/btn">
                                        Details <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-1" />
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-20 flex items-center justify-center gap-8 border-t border-slate-100 dark:border-slate-800 pt-12">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={pageSafe === 1}
                            className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors"
                        >
                            <ChevronLeft className="h-4 w-4" /> Zurück
                        </button>
                        <div className="text-xs font-black uppercase tracking-widest bg-slate-50 dark:bg-slate-900 px-4 py-2 rounded-xl border border-slate-100 dark:border-slate-800 tabular-nums">
                            {pageSafe} <span className="opacity-30 mx-1">/</span> {totalPages}
                        </div>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={pageSafe === totalPages}
                            className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors"
                        >
                            Weiter <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </Container>
        </div>
    );
}